/**
 * OnboardingFlow — Flujo de bienvenida de 10 pasos para nuevos usuarios de Arbo.
 * Pasos: Splash (0) → Nombre (1) → Cuenta (2) → Gasto (3) → Recurrente (4)
 *        → Tour x5 (5-9) → Bienvenida (10... inline en paso 9 como "Comenzar")
 */
import { useState, useRef, useEffect } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { ArboLogo } from "../ui";
import MoneyInput from "../inputs/MoneyInput";
import { AudioRecorder } from "../inputs";
import { api } from "../../services/api";
import { CATS_LIST } from "../../constants/categories";
import { track } from "../../lib/posthog";

const BANCOS = [
  "BBVA", "Banorte", "Santander", "HSBC", "Citibanamex",
  "Scotiabank", "Nu", "Hey Banco", "Rappi", "Stori", "Otro",
];

const TIPOS_CUENTA = [
  { value: "efectivo", label: "Efectivo", icon: "💵" },
  { value: "débito",   label: "Débito",   icon: "💳" },
  { value: "crédito",  label: "Crédito",  icon: "🔴" },
  { value: "vales",    label: "Vales",    icon: "🎟️" },
  { value: "ahorro",   label: "Ahorro",   icon: "🏦" },
];

const TOUR_SLIDES = [
  { icon: "📊", titulo: "Inicio",    desc: "Aquí ves tu capital y a dónde va tu dinero" },
  { icon: "🧠", titulo: "Análisis",  desc: "Analiza tus gastos por categoría y tendencias" },
  { icon: "🐿️", titulo: "Duky",     desc: "Tu asistente financiero con IA — habla o escribe" },
  { icon: "📅", titulo: "Anual",     desc: "Ve el panorama completo de todo tu año" },
  { icon: "⚙️", titulo: "Config",   desc: "Configura cuentas, recurrentes y presupuestos" },
];

/* ── Estilos compartidos ── */
const containerStyle = {
  minHeight: "100vh",
  background: P.bg,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FF,
  padding: 24,
};

const cardStyle = {
  background: P.surface,
  borderRadius: 20,
  padding: 32,
  maxWidth: 420,
  width: "100%",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
};

const btnPrimary = {
  background: P.ingresos.base,
  color: "#fff",
  border: "none",
  borderRadius: 12,
  padding: "14px 24px",
  fontSize: 16,
  fontWeight: 700,
  fontFamily: FF,
  cursor: "pointer",
  width: "100%",
  transition: "opacity .15s",
};

const btnSecondary = {
  background: "transparent",
  color: P.textSm,
  border: "none",
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: 500,
  fontFamily: FF,
  cursor: "pointer",
  width: "100%",
  marginTop: 4,
};

const label = {
  fontSize: 13,
  fontWeight: 600,
  color: P.textMd,
  marginBottom: 6,
  display: "block",
};

const fieldInput = { ...inputBase, marginBottom: 14 };

/* ── Componente principal ── */
export default function OnboardingFlow({
  onComplete, createCuenta, setRecurrentes, recurrentes, setMonth, cuentas, mobile,
}) {
  const [step, setStep] = useState(0);
  const [animDir, setAnimDir] = useState(1); // 1 = forward, -1 = back
  const [visible, setVisible] = useState(true);

  // Paso 1
  const [nombre, setNombre] = useState("");

  // Paso 2
  const [cuentaTipo, setCuentaTipo] = useState("débito");
  const [cuentaNombre, setCuentaNombre] = useState("");
  const [cuentaBanco, setCuentaBanco] = useState("BBVA");
  const [cuentaSaldo, setCuentaSaldo] = useState(0);
  const [cuentaLimite, setCuentaLimite] = useState(0);
  const [cuentaCorte, setCuentaCorte] = useState("");
  const [cuentaPago, setCuentaPago] = useState("");
  const [cuentaError, setCuentaError] = useState("");
  const [cuentaCreada, setCuentaCreada] = useState(null);
  const [creandoCuenta, setCreandoCuenta] = useState(false);

  // Paso 3
  const [gastoMode, setGastoMode] = useState(null); // null | "voz" | "manual"
  const [gastoConcepto, setGastoConcepto] = useState("");
  const [gastoMonto, setGastoMonto] = useState(0);
  const [gastoCat, setGastoCat] = useState("Otros");
  const [gastoGuardado, setGastoGuardado] = useState(false);

  // Paso 4
  const [recConcepto, setRecConcepto] = useState("");
  const [recMonto, setRecMonto] = useState(0);
  const [recDia, setRecDia] = useState("");
  const [recCuentaId, setRecCuentaId] = useState("");
  const [recGuardado, setRecGuardado] = useState(false);

  // Paso 5-9 (Tour)
  const [tourIdx, setTourIdx] = useState(0);

  // Guardando
  const [saving, setSaving] = useState(false);

  // Trackear cada step que el usuario inicia (skip step 0 = splash)
  useEffect(() => {
    if (step > 0) {
      try { track("onboarding_step_started", { step }); } catch {}
    }
  }, [step]);

  const goTo = (target) => {
    // Si estamos avanzando, marcar el step actual como completado
    if (target > step && step > 0) {
      try { track("onboarding_step_completed", { step }); } catch {}
    }
    setAnimDir(target > step ? 1 : -1);
    setVisible(false);
    setTimeout(() => {
      setStep(target);
      setVisible(true);
    }, 220);
  };

  const next = () => goTo(step + 1);
  const skip = () => goTo(step + 1);

  /* ── Handlers ── */

  async function handleCrearCuenta() {
    if (!cuentaNombre.trim()) { setCuentaError("El nombre es obligatorio"); setTimeout(() => setCuentaError(""), 3000); return; }
    setCreandoCuenta(true);
    setCuentaError("");
    try {
      const payload = {
        nombre: cuentaNombre.trim(),
        tipo: cuentaTipo,
        banco: cuentaTipo !== "efectivo" ? cuentaBanco : null,
        saldo_inicial: cuentaSaldo || 0,
        rastrear_saldo: true,
      };
      if (cuentaTipo === "crédito") {
        payload.limite_credito = cuentaLimite || null;
        payload.fecha_corte = cuentaCorte ? Number(cuentaCorte) : null;
        payload.fecha_limite_pago = cuentaPago ? Number(cuentaPago) : null;
      }
      const result = await createCuenta(payload);
      setCuentaCreada(result);
      next();
    } catch (e) {
      console.error("Error creando cuenta:", e);
      setCuentaError("Error al crear la cuenta. Intenta de nuevo.");
      setTimeout(() => setCuentaError(""), 3000);
    } finally {
      setCreandoCuenta(false);
    }
  }

  async function handleGuardarGastoManual() {
    if (!gastoConcepto.trim() || !gastoMonto) return;
    const cuentaId = cuentaCreada?.id || null;
    try {
      // Obtener mesId del mes actual
      const yr = new Date().getFullYear();
      const mesNum = new Date().getMonth() + 1;
      const mesData = await api.meses.get(yr, mesNum);
      const mesId = mesData?.id;
      if (!mesId) throw new Error("No mes_id");
      // Guardar directo al backend
      const creado = await api.gastos.extras.create({
        mes_id: mesId,
        concepto: gastoConcepto.trim(),
        monto: gastoMonto,
        categoria: gastoCat || "Otros",
        cuenta_id: cuentaId,
        fecha: new Date().toISOString().slice(0, 10),
      });
      // NO se toca el estado local del mes. Antes se hacía un setMonth con un
      // crypto.randomUUID() nuevo: syncArrays no lo encontraba en
      // lastSyncedRef, lo tomaba por un alta pendiente y hacía un SEGUNDO
      // POST — el primer gasto de todo usuario quedaba duplicado en la base.
      // El gasto ya está en el backend; `onComplete` refresca el mes al salir.
      void creado;
    } catch (e) {
      console.error("Onboarding gasto save failed:", e);
    }
    setGastoGuardado(true);
    setTimeout(() => next(), 500);
  }

  async function handleVozConfirm(transacciones) {
    try {
      const yr = new Date().getFullYear();
      const mesNum = new Date().getMonth() + 1;
      const mesData = await api.meses.get(yr, mesNum);
      const mesId = mesData?.id;
      const cuentaId = cuentaCreada?.id || null;
      const hoy = new Date().toISOString().slice(0, 10);

      for (const tx of transacciones) {
        const fecha = tx.fecha || hoy;
        if (tx.tipo === "gasto_extra" || tx.tipo === "gasto") {
          await api.gastos.extras.create({
            mes_id: mesId,
            concepto: tx.concepto,
            monto: tx.monto,
            categoria: tx.categoria || "Otros",
            cuenta_id: cuentaId,
            fecha,
          });
        } else if (tx.tipo === "ingreso") {
          await api.ingresos.create({
            mes_id: mesId,
            concepto: tx.concepto,
            monto: tx.monto,
            cuenta_id: cuentaId,
            fecha,
          });
        }
      }
      setGastoGuardado(true);
    } catch (e) {
      console.error("Onboarding voz save failed:", e);
    }
    setGastoMode(null);
    next();
  }

  async function handleAgregarRecurrente() {
    if (!recConcepto.trim() || !recMonto) return;
    const payload = {
      concepto: recConcepto.trim(),
      monto: recMonto,
      tipo: "fijo",
      categoria: "Otros",
      activo: true,
      dia_mes: recDia ? Number(recDia) : null,
      cuenta_id: recCuentaId || null,
    };
    try {
      const res = await api.recurrentes.create(payload);
      const created = Array.isArray(res) ? res[0] : res;
      if (created) {
        setRecurrentes(prev => [...prev, {
          id: created.id, concepto: created.concepto, monto: created.monto,
          tipo: created.tipo, cat: created.categoria || "Otros",
          activo: true, dia_mes: created.dia_mes, cuenta_id: created.cuenta_id,
        }]);
      }
    } catch (e) {
      console.error("Onboarding recurrente save failed:", e);
    }
    setRecGuardado(true);
    setTimeout(() => next(), 400);
  }

  async function handleCompletar() {
    setSaving(true);
    try { track("onboarding_completed"); } catch {}
    try {
      await api.preferencias.upsert("nombre_usuario", nombre.trim() || "Arbolero");
      await api.preferencias.upsert("onboarding_completado", "true");
    } catch (e) {
      console.error("Error guardando preferencias:", e);
    } finally {
      setSaving(false);
      onComplete();
    }
  }

  /* ── Transición ── */
  const transitionStyle = {
    transition: "opacity 0.22s ease, transform 0.22s ease",
    opacity: visible ? 1 : 0,
    transform: visible ? "translateX(0)" : `translateX(${animDir * 30}px)`,
  };

  /* ═══════════════════════════════ RENDER POR PASO ═══════════════════════════════ */

  // Paso 0 — Splash
  if (step === 0) return (
    <div style={containerStyle}>
      <div style={{ ...transitionStyle, textAlign: "center", maxWidth: 340 }}>
        <div style={{ marginBottom: 32, display: "flex", justifyContent: "center" }}>
          <ArboLogo size={72} showText animated />
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: P.text, margin: "0 0 12px", fontFamily: FF }}>
          Toma el control de tu dinero
        </h1>
        <p style={{ fontSize: 16, color: P.textMd, margin: "0 0 40px", lineHeight: 1.5 }}>
          En 2 minutos tendrás todo listo para saber exactamente a dónde va cada peso.
        </p>
        <button style={btnPrimary} onClick={next}>
          ¡Vamos!
        </button>
      </div>
    </div>
  );

  // Paso 1 — Nombre
  if (step === 1) return (
    <div style={containerStyle}>
      <div style={{ ...transitionStyle, ...cardStyle }}>
        <div style={{ marginBottom: 24 }}>
          <ArboLogo size={28} showText={false} />
        </div>
        <StepDots total={5} current={0} />
        <h2 style={{ fontSize: 22, fontWeight: 800, color: P.text, margin: "20px 0 8px", fontFamily: FF }}>
          ¿Cómo quieres que te diga Duky?
        </h2>
        <p style={{ fontSize: 14, color: P.textSm, margin: "0 0 24px" }}>
          Puede ser tu nombre, apodo o como tú prefieras 🐿️
        </p>
        <label style={label}>Tu apodo o nombre</label>
        <input
          style={fieldInput}
          type="text"
          placeholder="Ej. Charly, Mamá, Jefe…"
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          onKeyDown={e => e.key === "Enter" && nombre.trim() && next()}
          autoFocus
        />
        <button style={{ ...btnPrimary, opacity: nombre.trim() ? 1 : 0.5 }} onClick={next} disabled={!nombre.trim()}>
          Continuar
        </button>
        <button style={btnSecondary} onClick={() => { setNombre(""); next(); }}>
          Saltar
        </button>
      </div>
    </div>
  );

  // Paso 2 — Cuenta
  if (step === 2) return (
    <div style={containerStyle}>
      <div style={{ ...transitionStyle, ...cardStyle }}>
        <div style={{ marginBottom: 16 }}>
          <ArboLogo size={24} showText={false} />
        </div>
        <StepDots total={5} current={1} />
        <h2 style={{ fontSize: 20, fontWeight: 800, color: P.text, margin: "20px 0 6px", fontFamily: FF }}>
          ¿De dónde sale tu dinero?
        </h2>
        <p style={{ fontSize: 14, color: P.textSm, margin: "0 0 20px" }}>
          Registra la cuenta que más usas para que Arbo te ayude a organizar tu dinero.
        </p>

        {/* Tipo */}
        <label style={label}>Tipo de cuenta</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {TIPOS_CUENTA.map(t => (
            <button
              key={t.value}
              onClick={() => setCuentaTipo(t.value)}
              style={{
                padding: "7px 12px",
                borderRadius: 10,
                border: `2px solid ${cuentaTipo === t.value ? P.ingresos.base : P.border}`,
                background: cuentaTipo === t.value ? P.ingresos.pastel : P.surface,
                color: cuentaTipo === t.value ? P.ingresos.base : P.textMd,
                fontWeight: 600,
                fontSize: 13,
                fontFamily: FF,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Nombre */}
        <label style={label}>Nombre de la cuenta</label>
        <input
          style={fieldInput}
          type="text"
          placeholder='Ej. "Mi débito BBVA"'
          value={cuentaNombre}
          onChange={e => setCuentaNombre(e.target.value)}
        />

        {/* Banco */}
        {cuentaTipo !== "efectivo" && (
          <>
            <label style={label}>Banco</label>
            <select
              style={{ ...fieldInput }}
              value={cuentaBanco}
              onChange={e => setCuentaBanco(e.target.value)}
            >
              {BANCOS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </>
        )}

        {/* Saldo inicial */}
        <label style={label}>{cuentaTipo === "crédito" ? "Saldo usado actualmente" : "Saldo inicial"}</label>
        <MoneyInput
          style={fieldInput}
          value={cuentaSaldo}
          onChange={setCuentaSaldo}
          placeholder="0"
        />

        {/* Campos TDC */}
        {cuentaTipo === "crédito" && (
          <div style={{ background: P.bg, borderRadius: 12, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: P.ingresos.base, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, fontFamily: FF }}>Datos de tarjeta</div>
            <label style={label}>Límite de crédito</label>
            <MoneyInput style={fieldInput} value={cuentaLimite} onChange={setCuentaLimite} placeholder="50,000" />
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={label}>Día de corte</label>
                <select value={cuentaCorte} onChange={e => setCuentaCorte(e.target.value)} style={{ ...fieldInput, appearance: "auto" }}>
                  <option value="">--</option>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>Día {i + 1}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Día límite de pago</label>
                <select value={cuentaPago} onChange={e => setCuentaPago(e.target.value)} style={{ ...fieldInput, appearance: "auto" }}>
                  <option value="">--</option>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>Día {i + 1}</option>)}
                </select>
              </div>
            </div>
            {cuentaCorte && cuentaPago && (
              <div style={{ padding: "8px 10px", background: "#FFFBEB", borderRadius: 8, border: "1px solid #FDE68A", marginTop: 4, fontSize: 11, color: "#92400E", fontFamily: FF, lineHeight: 1.4 }}>
                📋 Compras del día {Number(cuentaCorte) + 1 > 31 ? 1 : Number(cuentaCorte) + 1} al {cuentaCorte} se pagan antes del día {cuentaPago} {Number(cuentaPago) < Number(cuentaCorte) ? "del mes siguiente" : "del mismo mes"}.
              </div>
            )}
          </div>
        )}

        {cuentaError && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#DC2626", fontFamily: FF, fontWeight: 600 }}>{cuentaError}</div>
        )}

        <button
          style={{ ...btnPrimary, opacity: cuentaNombre.trim() && !creandoCuenta ? 1 : 0.5 }}
          onClick={handleCrearCuenta}
          disabled={!cuentaNombre.trim() || creandoCuenta}
        >
          {creandoCuenta ? "Creando..." : "Crear cuenta"}
        </button>
        <button style={btnSecondary} onClick={next}>
          Saltar por ahora
        </button>
      </div>
    </div>
  );

  // Paso 3 — Gasto (voz o manual)
  if (step === 3) {
    // Si abrió voz
    if (gastoMode === "voz") return (
      <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <AudioRecorder
          mobile={mobile}
          onClose={() => setGastoMode(null)}
          onConfirm={handleVozConfirm}
        />
      </div>
    );

    return (
      <div style={containerStyle}>
        <div style={{ ...transitionStyle, ...cardStyle }}>
          <div style={{ marginBottom: 16 }}>
            <ArboLogo size={24} showText={false} />
          </div>
          <StepDots total={5} current={2} />
          <h2 style={{ fontSize: 20, fontWeight: 800, color: P.text, margin: "20px 0 6px", fontFamily: FF }}>
            ¿En qué gastaste hoy?
          </h2>
          <p style={{ fontSize: 14, color: P.textSm, margin: "0 0 24px" }}>
            Prueba registrarlo con tu voz o escríbelo — tú decides.
          </p>

          {!gastoMode && (
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <button
                style={{ ...btnPrimary, flex: 1, padding: "16px 12px", fontSize: 14 }}
                onClick={() => setGastoMode("voz")}
              >
                🎙️ Dictar con voz
              </button>
              <button
                style={{ ...btnPrimary, flex: 1, padding: "16px 12px", fontSize: 14, background: P.ingresos.mid }}
                onClick={() => setGastoMode("manual")}
              >
                ✏️ Escribirlo
              </button>
            </div>
          )}

          {gastoMode === "manual" && (
            <>
              <label style={label}>Concepto</label>
              <input
                style={fieldInput}
                type="text"
                placeholder='Ej. "Tacos de canasta"'
                value={gastoConcepto}
                onChange={e => setGastoConcepto(e.target.value)}
              />

              <label style={label}>Monto</label>
              <MoneyInput
                style={fieldInput}
                value={gastoMonto}
                onChange={setGastoMonto}
                placeholder="0"
              />

              <label style={label}>Categoría</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
                {CATS_LIST.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setGastoCat(c.id)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 8,
                      border: `2px solid ${gastoCat === c.id ? c.color : P.border}`,
                      background: gastoCat === c.id ? c.color + "22" : P.surface,
                      color: gastoCat === c.id ? c.color : P.textMd,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: FF,
                      cursor: "pointer",
                    }}
                  >
                    {c.icon} {c.id}
                  </button>
                ))}
              </div>

              {cuentaCreada && (
                <div style={{ fontSize: 13, color: P.textSm, marginBottom: 14 }}>
                  Cuenta: <strong>{cuentaCreada.nombre}</strong>
                </div>
              )}

              <button
                style={{ ...btnPrimary, opacity: gastoConcepto.trim() && gastoMonto ? 1 : 0.5 }}
                onClick={handleGuardarGastoManual}
                disabled={!gastoConcepto.trim() || !gastoMonto}
              >
                {gastoGuardado ? "✓ Guardado" : "Guardar gasto"}
              </button>
            </>
          )}

          <button style={btnSecondary} onClick={next}>
            Saltar
          </button>
        </div>
      </div>
    );
  }

  // Paso 4 — Gasto recurrente
  if (step === 4) return (
    <div style={containerStyle}>
      <div style={{ ...transitionStyle, ...cardStyle }}>
        <div style={{ marginBottom: 16 }}>
          <ArboLogo size={24} showText={false} />
        </div>
        <StepDots total={5} current={3} />
        <h2 style={{ fontSize: 20, fontWeight: 800, color: P.text, margin: "20px 0 6px", fontFamily: FF }}>
          ¿Qué pagas cada mes sí o sí?
        </h2>
        <p style={{ fontSize: 14, color: P.textSm, margin: "0 0 20px" }}>
          Renta, Netflix, gimnasio... Arbo los registra automáticamente cada mes.
        </p>

        <label style={label}>Concepto</label>
        <input
          style={fieldInput}
          type="text"
          placeholder='Ej. "Netflix"'
          value={recConcepto}
          onChange={e => setRecConcepto(e.target.value)}
        />

        <label style={label}>Monto mensual</label>
        <MoneyInput
          style={fieldInput}
          value={recMonto}
          onChange={setRecMonto}
          placeholder="0"
        />

        <label style={label}>Día del mes (opcional)</label>
        <input
          style={fieldInput}
          type="number"
          placeholder="Ej. 15"
          min="1"
          max="31"
          value={recDia}
          onChange={e => setRecDia(e.target.value)}
        />

        {cuentaCreada && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: P.textMd, marginBottom: 16, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={recCuentaId === cuentaCreada.id}
              onChange={e => setRecCuentaId(e.target.checked ? cuentaCreada.id : "")}
            />
            Asociar a <strong>{cuentaCreada.nombre}</strong>
          </label>
        )}

        <button
          style={{ ...btnPrimary, opacity: recConcepto.trim() && recMonto ? 1 : 0.5 }}
          onClick={handleAgregarRecurrente}
          disabled={!recConcepto.trim() || !recMonto}
        >
          {recGuardado ? "✓ Agregado" : "Agregar recurrente"}
        </button>
        <button style={btnSecondary} onClick={next}>
          Saltar
        </button>
      </div>
    </div>
  );

  // Paso 5 — Mensaje de cierre, el tour contextual se activa en la app real
  if (step >= 5) {
    return (
      <div style={containerStyle}>
        <div style={{ ...transitionStyle, ...cardStyle, textAlign: "center" }}>
          <ArboLogo size={48} showText animated />
          <h2 style={{ fontSize: 22, fontWeight: 800, color: P.text, margin: "20px 0 12px", fontFamily: FF }}>
            ¡Listo{nombre ? `, ${nombre}` : ""}!
          </h2>
          <p style={{ fontSize: 15, color: P.textMd, margin: "0 0 32px", lineHeight: 1.6 }}>
            Ya tienes todo configurado. Ahora te mostraré las secciones clave para que le saques el máximo provecho.
          </p>
          <button style={btnPrimary} onClick={handleCompletar} disabled={saving}>
            {saving ? "Guardando..." : "Comenzar"}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/* ── Indicador de pasos ── */
function StepDots({ total, current }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: i === current ? 20 : 8,
            height: 8,
            borderRadius: 4,
            background: i === current ? P.ingresos.base : i < current ? P.ingresos.tint : P.border,
            transition: "all .2s",
          }}
        />
      ))}
    </div>
  );
}
